import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';

/** One sort choice. `Label` is the plain option text — the `Sort: ` prefix belongs to the trigger, not the option. */
export interface AGUSortOption {
    readonly Key: string;
    readonly Label: string;
}

/**
 * The finder toolbar's sort control — the mockup's `.selwrap` chip, whose closed
 * state reads `Sort: Best match`.
 *
 * It replaces the second `mj-filter-popover` the toolbar used to open onto a
 * radio group: a sort pick is one complete choice, and the mockup states the
 * current one on the control itself rather than making the member open a panel
 * to find out how the results are ordered.
 *
 * A native `<select>` (which is what the mockup literally uses) was not an
 * option: its option list cannot carry the accent styling, and the prefix would
 * have had to live inside the first option's text, where it would be read out
 * as part of every other option's siblings. A listbox states it once.
 */
@Component({
    standalone: false,
    selector: 'agu-sort-trigger',
    templateUrl: './sort-trigger.component.html',
    styleUrl: './sort-trigger.component.scss',
})
export class AGUSortTriggerComponent {
    @Input() Options: readonly AGUSortOption[] = [];
    /** Key of the option in force. */
    @Input() Value = '';
    /** Accessible name for the menu, e.g. 'Sort results'. */
    @Input() MenuLabel = 'Sort results';
    @Output() ValueChange = new EventEmitter<string>();

    Open = false;

    constructor(private readonly host: ElementRef<HTMLElement>) {}

    /** The option in force, falling back to the first — a finder always sorts by something. */
    get ActiveLabel(): string {
        return (this.Options.find((option) => option.Key === this.Value) ?? this.Options[0])?.Label ?? '';
    }

    get TriggerText(): string {
        return `Sort: ${this.ActiveLabel}`;
    }

    IsSelected(option: AGUSortOption): boolean {
        return option.Key === this.Value;
    }

    Toggle(): void {
        this.Open = !this.Open;
    }

    /** A pick is a complete choice: emit and close, so the member sees the reordered results rather than the panel over them. */
    Pick(key: string): void {
        this.Open = false;
        this.ValueChange.emit(key);
    }

    @HostListener('document:keydown.escape') OnEscape(): void {
        this.Open = false;
    }

    /** Close on any click that lands outside the control (same pattern as the sidebar account menu). */
    @HostListener('document:click', ['$event']) OnDocumentClick(event: MouseEvent): void {
        if (!this.Open) {
            return;
        }
        const root = this.host.nativeElement;
        const target = event.target;
        if (target instanceof Node && root.contains(target)) {
            return;
        }
        this.Open = false;
    }
}
